import React from "react";
import { QuestionResponse } from "@/pages/deals/types";
import { iconSize } from "@/components/ui/icon-container";
import { useIndicationData } from "./hooks";
import DreamerModeIndication from "./components/DreamerModeIndication";
import ModifiedByDreamerIndication from "./components/ModifiedByDreamerIndication";
import ModifiedManuallyIndication from "./components/ModifiedManuallyIndication";
import MismatchIndication from "./components/MismatchIndication/MismatchIndication";

interface Props {
  dealId: string;
  questionnaireId: string;
  questionId: string;
  questionResponse: QuestionResponse | null;
  restorePreviousVersionButton?: boolean;
  showSources?: boolean;
  size?: iconSize;
  // False when a value is stored but the field can't display it, e.g. a
  // person the picker no longer lists.
  isValueDisplayable?: boolean;
}

const DreamerIndication: React.FC<Props> = ({
  dealId,
  questionnaireId,
  questionId,
  questionResponse,
  restorePreviousVersionButton = true,
  showSources = true,
  size,
  isValueDisplayable = true
}) => {
  const indicationData = useIndicationData(questionResponse);

  const {
    lastEditedByData,
    isLastEditedByDreamer,
    hasRevisions,
    hasMismatch,
    revisionMadeByDreamer
  } = indicationData;

  const responseValue = questionResponse?.response?.value;

  // Check if filled by Dreamer at all
  const isFilledByDreamer =
    isLastEditedByDreamer || Boolean(revisionMadeByDreamer);

  // Check for empty values
  const hasEmptyValue =
    responseValue === null ||
    responseValue === undefined ||
    responseValue === "" ||
    (Array.isArray(responseValue) && responseValue.length === 0);

  if (!isFilledByDreamer && !hasMismatch) return null;
  // Hide indicator for empty values, unless a user cleared a Dreamer-populated
  // field — in that case revisionMadeByDreamer is truthy and we keep the
  // indicator visible so the user can restore the previous Dreamer version.
  if (hasEmptyValue && !hasMismatch && !revisionMadeByDreamer) return null;
  // A stored value the field can't display reads as empty to the user, so it
  // gets no indication either, unless Dreamer is suggesting a different value.
  if (!isValueDisplayable && !hasMismatch) return null;

  // Determine which scenario applies
  const dreamerMode =
    isLastEditedByDreamer && (!hasRevisions || Boolean(revisionMadeByDreamer));

  const isModifiedByDreamer =
    isLastEditedByDreamer && hasRevisions && !Boolean(revisionMadeByDreamer);

  const modifiedManuallyMode =
    Boolean(lastEditedByData) && !isLastEditedByDreamer;

  const commonProps = {
    dealId,
    questionnaireId,
    questionId,
    questionResponse,
    restorePreviousVersionButton,
    showSources,
    size,
    indicationData
  };

  if (hasMismatch) {
    return <MismatchIndication {...commonProps} />;
  }

  if (dreamerMode) {
    return <DreamerModeIndication {...commonProps} />;
  }

  if (isModifiedByDreamer) {
    return <ModifiedByDreamerIndication {...commonProps} />;
  }

  if (modifiedManuallyMode) {
    return <ModifiedManuallyIndication {...commonProps} />;
  }

  return null;
};

export default DreamerIndication;
